import React, { forwardRef, useContext, useEffect, useImperativeHandle, useState } from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import { Variable } from 'src/api/types';
import { useStateSelector } from 'src/redux';
import { ColorSet } from 'src/redux/reducers/theme';
import { formatNumber } from 'src/utils/misc';
import { GradientText, InputLabel, Poppins } from 'src/common';
import { CurrencyInputFormula } from 'src/components/form/CurrencyInput';
import Input from 'src/components/form/Input';
import { VariablesContext } from '../context';
import { getVariableIcon } from './variableIcon';

export interface SuggestionListProps {
  items: Variable[];
  command: (attrs: { id: number; label: string; value?: number; currency?: boolean }) => void;
  onAddWorkspaceVariable?: () => void;
  onAddAssessmentVariable?: () => void;
  onSaveVariableValue?: (config: { variableId: number; value: number; currency: boolean }) => void | Promise<void>;
  // Bridge to the suggestion render() lifecycle so the popup survives the editor blur that
  // happens when the inline value-edit input takes focus (Phase 0 finding).
  setEditing?: (editing: boolean) => void;
  closePopup?: () => void;
}

export interface SuggestionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

const Menu = styled.div<{ $colors: ColorSet }>`
  min-width: 680px;
  max-height: 400px;
  overflow-y: auto;
  font-size: 14px; /* portaled to body, so set explicitly rather than inherit the larger base */
  border: 1px solid ${({ $colors }) => $colors.stroke};
  border-radius: 6px;
  background: ${({ $colors }) => $colors.white};
  filter: drop-shadow(0px 0px 20px ${({ $colors }) => $colors.shadowLight});
  padding: 10px 8px;
  z-index: 2;

  .fi-dropdown-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: sticky;
    top: -10px;
    z-index: 1;
    background: ${({ $colors }) => $colors.white};
    margin: -10px -8px 4px -8px;
    padding: 10px 8px 8px 26px;

    &__label {
      font-family: Poppins, sans-serif;
      font-size: 14px;
      font-weight: 600;
      color: ${({ $colors }) => $colors.prussianBlue};
    }
    &__actions {
      display: flex;
      gap: 16px;
    }
    &__btn {
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      font-family: Poppins, sans-serif;
      font-size: 14px;
      font-weight: 500;
      background-image: ${({ $colors }) => $colors.gradient};
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      &:hover {
        text-decoration: underline;
      }
    }
  }

  .fi-row {
    display: grid;
    grid-template-columns: 12px 1fr auto;
    grid-gap: 6px;
    align-items: center;
    border-radius: 4px;
    font-weight: 500;
    padding: 4px 8px;
    cursor: pointer;

    &__name {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 350px;
      margin-right: 10px;
      color: ${({ $colors }) => $colors.davysGrey};
    }
    &__value {
      justify-self: end;
      color: ${({ $colors }) => $colors.prussianBlue};
    }
    &__edit {
      justify-self: end;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    &--active {
      background: ${({ $colors }) => $colors.gradient};
      color: ${({ $colors }) => $colors.white};
      .fi-row__name,
      .fi-row__value {
        color: ${({ $colors }) => $colors.white};
      }
      /* "+ Add Value" is a GradientText (gradient fill via transparent text); flatten it to
         solid white so it's legible on the gradient-highlighted row. */
      .fi-add-value,
      .fi-add-value * {
        background: none !important;
        -webkit-background-clip: unset !important;
        -webkit-text-fill-color: ${({ $colors }) => $colors.white} !important;
        color: ${({ $colors }) => $colors.white} !important;
      }
      path {
        fill: ${({ $colors }) => $colors.white};
      }
    }
  }

  .fi-edit-actions {
    display: flex;
    align-items: center;
    gap: 14px;
  }
  .fi-edit-btn {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

export const SuggestionList = forwardRef<SuggestionListRef, SuggestionListProps>(
  ({ items, command, onAddWorkspaceVariable, onAddAssessmentVariable, onSaveVariableValue, setEditing, closePopup }, ref) => {
    const colors = useStateSelector(({ theme }) => theme.colors);
    const { workspaceVariableNames } = useContext(VariablesContext);
    const [selected, setSelected] = useState(0);

    const [editingId, setEditingId] = useState<number | null>(null);
    const [editValue, setEditValue] = useState<number | undefined>(undefined);
    const [editCurrency, setEditCurrency] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => setSelected(0), [items]);

    const pick = (v: Variable) => command({ id: v.id, label: v.alias || v.name });

    const startEdit = (v: Variable) => {
      setEditing?.(true); // SYNC: keep popup alive when the editor blurs as the input focuses
      setEditCurrency(v.currency);
      setEditValue(undefined);
      setEditingId(v.id);
    };
    const cancel = () => {
      setEditingId(null);
      setEditValue(undefined);
      setEditing?.(false);
      closePopup?.();
    };
    const save = async (v: Variable) => {
      if (editValue === undefined) return;
      setIsSaving(true);
      try {
        await onSaveVariableValue?.({ variableId: v.id, value: editValue, currency: editCurrency });
        setEditing?.(false);
        // carry value/currency so the chip shows immediately, before the variables refetch lands
        command({ id: v.id, label: v.alias || v.name, value: editValue, currency: editCurrency });
        closePopup?.();
      } finally {
        setIsSaving(false);
      }
    };

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        if (editingId !== null) return false; // let the inline input handle keys
        if (event.key === 'ArrowDown') {
          setSelected((s) => (items.length ? (s + 1) % items.length : 0));
          return true;
        }
        if (event.key === 'ArrowUp') {
          setSelected((s) => (items.length ? (s + items.length - 1) % items.length : 0));
          return true;
        }
        if (event.key === 'Enter') {
          if (items[selected]) pick(items[selected]);
          return true;
        }
        return false;
      },
    }));

    const saveDisabled = isSaving || editValue === undefined;

    return (
      <Menu
        $colors={colors}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="fi-dropdown-header">
          <span className="fi-dropdown-header__label">VARIABLES</span>
          <div className="fi-dropdown-header__actions">
            {onAddWorkspaceVariable && (
              <button
                type="button"
                className="fi-dropdown-header__btn"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onAddWorkspaceVariable();
                }}
              >
                + Workspace Variable
              </button>
            )}
            {onAddAssessmentVariable && (
              <button
                type="button"
                className="fi-dropdown-header__btn"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onAddAssessmentVariable();
                }}
              >
                + Assessment Variable
              </button>
            )}
          </div>
        </div>

        {items.length === 0 && <div style={{ padding: '6px 8px', opacity: 0.6 }}>No matches</div>}

        {items.map((v, i) => {
          const isEditing = editingId === v.id;
          const noData = _.isNil(v.value);
          return (
            <div
              key={v.id}
              data-cy={`fi-suggestion-${v.id}`}
              className={`fi-row ${i === selected && !isEditing ? 'fi-row--active' : ''}`}
              onMouseEnter={() => setSelected(i)}
              onClick={() => {
                if (!isEditing) pick(v);
              }}
            >
              <span style={{ display: 'inline-flex', justifySelf: 'center' }}>
                {getVariableIcon(v, workspaceVariableNames)}
              </span>
              <span className="fi-row__name">{v.alias || v.name}</span>

              {!noData ? (
                <span className="fi-row__value">
                  {formatNumber(v.value, { symbol: !!v.currency, maxDecimals: 3 })}
                </span>
              ) : isEditing ? (
                <span className="fi-row__edit" onClick={(e) => e.stopPropagation()}>
                  <InputLabel>CURRENCY</InputLabel>
                  <div
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setEditCurrency((c) => !c);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <Input
                      checked={editCurrency}
                      onChange={() => {}}
                      className="switch switch--small"
                      type="checkbox"
                      style={{ pointerEvents: 'none' }}
                    />
                  </div>
                  <CurrencyInputFormula
                    autoFocus
                    currency={editCurrency}
                    disabled={isSaving}
                    value={editValue ?? ''}
                    onValueChange={({ floatValue }) => setEditValue(floatValue)}
                  />
                  <div className="fi-edit-actions">
                    <button
                      type="button"
                      className="fi-edit-btn"
                      disabled={isSaving}
                      onMouseDown={(e) => {
                        if (isSaving) return;
                        e.preventDefault();
                        e.stopPropagation();
                        cancel();
                      }}
                    >
                      <Poppins px={14} weight={500}>
                        Cancel
                      </Poppins>
                    </button>
                    <button
                      type="button"
                      className="fi-edit-btn"
                      disabled={saveDisabled}
                      onMouseDown={(e) => {
                        if (saveDisabled) return;
                        e.preventDefault();
                        e.stopPropagation();
                        save(v);
                      }}
                    >
                      <GradientText px={14} weight={500} $noTransform>
                        Save
                      </GradientText>
                    </button>
                  </div>
                </span>
              ) : (
                <span
                  className="fi-add-value"
                  style={{ justifySelf: 'end' }}
                  onMouseDown={(e) => {
                    e.preventDefault(); // keep editor focused until the input mounts + autofocuses
                    e.stopPropagation();
                    startEdit(v);
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation(); // don't let the row insert a no-value chip
                  }}
                >
                  <GradientText px={14} weight={500} $noTransform>
                    + Add Value
                  </GradientText>
                </span>
              )}
            </div>
          );
        })}
      </Menu>
    );
  },
);

SuggestionList.displayName = 'SuggestionList';
